<template>
  <div id="app">
    <router-view />
    <!-- Support sessions (utils/support_session): the launch dialog, and the
         desktop the organization's app runs in. Outside the router view, so
         the admin page underneath stays put while a session runs. -->
    <launch-session-modal v-if="supportSessionLauncher" />
    <support-session-desktop v-if="supportSessionOpen" />
    <!-- "Install Screenkode Admin" (pwa/install_prompt), as in the
         organization apps. Not while a session's desktop covers the page. -->
    <pwa-install-prompt v-if="!supportSessionOpen" :lift-for-bottom-nav="bottomNavShown" />
  </div>
</template>
<script>
import { DEFAULT_ERROR_MSG, TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { CHECK_SUPPORT_SESSION, CLEAR_ERRORMSG, DISMISS_SUPPORT_SESSION } from '@/store/action_types';
import { DISPLAY_ROUTE } from '@/router/routes';
import launchSessionModal from '@/components/support_session/Launch_Session_Modal.vue';
import supportSessionDesktop from '@/components/support_session/Support_Session_Desktop.vue';
import PwaInstallPrompt from '@/components/Pwa_Install_Prompt.vue';
import { appShellState } from '@/utils/app_shell';
import { startTicketUnreadPolling, stopTicketUnreadPolling } from '@/utils/ticket_notifications';
import { startChatNotifications, stopChatNotifications } from '@/utils/chat_notifications';
// Quill rendering styles (.ql-editor) used to render saved template content on
// the Display and in screen previews. Kept global so they are present whatever
// route is active.
import '@vueup/vue-quill/dist/vue-quill.core.css';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

// Desktop UI density: the whole app renders at 90% (see index.css), as the
// frontend does. Never on the Display route — that view is shown on physical
// TVs and must render at its authored, pixel-exact size.
const APP_ZOOM_MIN_WIDTH = 1200;
const APP_ZOOM_CLASS = 'app-zoomed';

export default {
  components: {
    launchSessionModal,
    supportSessionDesktop,
    PwaInstallPrompt,
  },
  watch: {
    errorMessage: {
      handler(val) {
        if (val != null) {
          this.$toast.error(val ?? DEFAULT_ERROR_MSG, TOAST_GLOBAL_CONFIG);
          this.$store.dispatch(CLEAR_ERRORMSG);
        }
      },
    },
    route: {
      immediate: true,
      handler() {
        this.syncAppZoom();
      },
    },
    supportSessionOpen() {
      this.syncAppZoom();
    },
    // The unread ticket and chat counts (the badges on Tickets and Chat) are
    // kept current for whoever is signed in, and dropped when nobody is — or
    // on the Display route, which is a screen, not a person.
    account: {
      immediate: true,
      handler() {
        this.syncTicketPolling();
        this.syncChatNotifications();
      },
    },
    isDisplayRoute() {
      this.syncTicketPolling();
      this.syncChatNotifications();
    },
  },
  computed: {
    route() {
      return this.$route.name;
    },
    account() {
      return this.$store.getters.account.data;
    },
    supportSession() {
      return this.$store.getters.supportSession;
    },
    // The desktop is up: a session this admin is signed in for, launched here
    // or confirmed still open by the API after a reload.
    supportSessionOpen() {
      return Boolean(this.supportSession && this.$store.getters.supportSessionVerified && this.account);
    },
    supportSessionLauncher() {
      return this.$store.getters.supportSessionLauncher;
    },
    isDisplayRoute() {
      return this.route === DISPLAY_ROUTE;
    },
    // A page in the app shell is up, so on a phone its bottom nav is too.
    bottomNavShown() {
      return appShellState.count > 0;
    },
    errorMessage() {
      return this.$store.getters.errorMsg;
    },
  },
  methods: {
    syncTicketPolling() {
      if (this.account && this.$store.getters.sessionToken && !this.isDisplayRoute) startTicketUnreadPolling(this.$store, this.$router);
      else stopTicketUnreadPolling();
    },
    syncChatNotifications() {
      const store = this.$store;
      if (this.account && store.getters.sessionToken && !this.isDisplayRoute) {
        startChatNotifications(
          () => Boolean(store.getters.sessionToken && store.getters.account?.data),
          () => store.getters.sessionToken || null
        );
      } else {
        stopChatNotifications();
      }
    },
    shouldUseAppZoom() {
      if (typeof window === 'undefined') return false;
      // Never while a support session's desktop is up: the app in it sizes
      // itself, and would otherwise be scaled twice.
      return !this.isDisplayRoute && !this.supportSessionOpen && window.innerWidth >= APP_ZOOM_MIN_WIDTH;
    },
    syncAppZoom() {
      if (typeof document === 'undefined') return;
      document.documentElement.classList.toggle(APP_ZOOM_CLASS, this.shouldUseAppZoom());
    },
  },
  created() {
    window.addEventListener('resize', this.syncAppZoom);
    // A support session kept across a reload comes back only if it is still
    // open — and only for the admin who is signed in here.
    if (this.supportSession) {
      this.$store.dispatch(this.account ? CHECK_SUPPORT_SESSION : DISMISS_SUPPORT_SESSION);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.syncAppZoom);
    stopTicketUnreadPolling();
    stopChatNotifications();
    document.documentElement.classList.remove(APP_ZOOM_CLASS);
  },
};
</script>

<style lang="css">
@import './assets/style/index.css';
/* Admin layers, loaded after the base so they win ties with it. */
@import './assets/style/shell.css';
@import './assets/style/list.css';
@import './assets/style/modal.css';
/* Support ticket conversations, attachments and the unread badges. */
@import './assets/style/ticket.css';
/* The support chat (components/chat). */
@import './assets/style/chat.css';

/* Blank lines in rich-text content.
   An empty <p></p> collapses to zero height when saved HTML is rendered via
   v-html, so an authored blank line disappears. Legacy Quill stored
   <p><br></p>, which still renders. Give truly-empty paragraphs a line box so
   blank lines show everywhere content is rendered inside .ql-editor. */
.ql-editor p:empty::before {
  content: '\00a0';
}

/* The page underneath a support session's desktop must not scroll. */
body.ssd-open {
  overflow: hidden;
}

.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.spinner {
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-left-color: var(--color-primary);
  animation: spin 1s linear infinite;
}

.spinner img {
  height: 60px;
  z-index: 10; /* Ensures the image is above the spinner */
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
